import { Button, Input, Popconfirm } from 'antd';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';
import { BASE_URL, generateOrganizationApiToken, getOrganizationApiToken, revokeOrganizationApiToken } from 'utils/apiFunctions';

// HMS-78: on Heroku the API runs as a separate app (hms-api-*) - REACT_APP_INTEGRATION_API_BASE_URL, locally the main backend
const API_URL = `${process.env.REACT_APP_INTEGRATION_API_BASE_URL || BASE_URL}/api/v1`;

const maskToken = (token) => '•'.repeat(20) + token.slice(-6);

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    return false;
  }
}

// HMS-78: one API token per organization for the read-only integration API (/api/v1).
// The token is hidden by default (only its last 6 characters are shown) and revealed on a button click.
const OrganizationApiToken = ({ organizationId, apiTokenCreatedAt, reload }) => {
  const currentRole = useLoginStore(s => s.currentRole);
  const addMessage = useNotificationStore((state) => state.addMessage);
  const [token, setToken] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const call = async (fn) => {
    setIsBusy(true);
    try {
      const response = await fn(organizationId, currentRole);
      const body = await response.json();
      if (response.status !== 200) {
        throw new Error(body?.message ?? 'Error');
      }
      return body;
    } catch (e) {
      addMessage({ text: e.message, type: 'error' });
      return null;
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (!apiTokenCreatedAt) {
      setToken(null);
      return;
    }
    (async () => {
      const body = await call(getOrganizationApiToken);
      setToken(body?.token ?? null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, apiTokenCreatedAt]);

  const generate = async () => {
    const body = await call(generateOrganizationApiToken);
    if (body) {
      setToken(body.token);
      setIsRevealed(false);
      reload();
    }
  }

  const revoke = async () => {
    const body = await call(revokeOrganizationApiToken);
    if (body) {
      setToken(null);
      addMessage({ text: 'API token revoked', type: 'success' });
      reload();
    }
  }

  // each example is a complete command (concrete URL + token) - displayed with the token hidden/shown, copied with the full token
  const examples = [
    { title: 'Organization', path: '/organization' },
    { title: 'Finished games (latest 5)', path: '/games?status=FINISHED&limit=5' },
    { title: 'Games - next page (skip)', path: '/games?status=FINISHED&limit=50&skip=50' },
    { title: 'Polling (next changedSince = lastChangedAt of the last game)', path: '/games?status=FINISHED&changedSince=2026-01-01T00:00:00Z&limit=200' },
    { title: 'Game detail', path: '/game?gameId=<gameId>' },
    { title: 'Game events', path: '/gameEvents?gameId=<gameId>' },
    { title: 'Game lineup', path: '/gameLineup?gameId=<gameId>' },
    { title: 'Teams', path: '/teams' },
    { title: 'Players (selected ids)', path: '/players?ids=<playerId1>,<playerId2>' },
    { title: 'Venues', path: '/venues' },
    { title: 'Competitions, seasons, groups, phases', path: '/competitions' },
    { title: 'Sponsors', path: '/sponsors' },
  ];
  const curl = (path, tokenText) => `curl -s -H "Authorization: Bearer ${tokenText}" "${API_URL}${path}" | jq`;
  const displayedToken = token ? (isRevealed ? token : maskToken(token)) : '<token>';

  return (
    <div className="flex flex-col gap-4 p-4 max-w-[1200px]">
      <div>
        {apiTokenCreatedAt
          ? <>Token is <b>active</b>, generated {dayjs(apiTokenCreatedAt).format('D. M. YYYY HH:mm')}.</>
          : <>No token has been generated.</>
        }
      </div>

      {apiTokenCreatedAt
        ? (
          <div className="flex gap-2">
            <Input
              value={token ? (isRevealed ? token : maskToken(token)) : ''}
              placeholder={isBusy ? '' : 'The token cannot be displayed (generated before it could be stored for displaying) - regenerate it'}
              readOnly
              className="font-mono"
            />
            {token && <Button onClick={() => setIsRevealed(r => !r)}>{isRevealed ? 'Hide' : 'Show'}</Button>}
            {token && <CopyToClipboardButton onClick={() => copyText(token)} />}
            <Popconfirm
              title="Regenerate API token"
              description="The current token will stop working immediately."
              okText="Regenerate"
              okButtonProps={{ danger: true }}
              onConfirm={generate}
            >
              <Button loading={isBusy}>Regenerate</Button>
            </Popconfirm>
            <Popconfirm
              title="Revoke API token"
              description="Applications using this token will lose access immediately."
              okText="Revoke"
              okButtonProps={{ danger: true }}
              onConfirm={revoke}
            >
              <Button danger loading={isBusy}>Revoke</Button>
            </Popconfirm>
          </div>
        )
        : (
          <div>
            <Button type="primary" loading={isBusy} onClick={generate}>Generate token</Button>
          </div>
        )
      }

      <div className="flex flex-col gap-1">
        <b>Rate limits</b>
        <ul className="list-disc pl-5 m-0">
          <li><b>120 requests per minute</b> per token (organization).</li>
          <li><b>300 requests per minute</b> per IP address (all requests, incl. invalid tokens).</li>
          <li>Over the limit the API returns <code>429 Too Many Requests</code> with the header <code>Retry-After</code> (seconds until the limit resets).</li>
          <li>Every response carries <code>RateLimit-Limit</code>, <code>RateLimit-Remaining</code> and <code>RateLimit-Reset</code> (seconds).</li>
          <li>Recommended usage: load the data once, then set the webhook below and load the details only of the games it reports - no regular polling is needed. Large lists page with <code>limit</code> (max 500) and <code>skip</code>.</li>
        </ul>
      </div>

      <div className="flex flex-col gap-2">
        <b>Examples</b>
        {examples.map(example => (
          <div key={example.path} className="flex gap-2 items-center">
            <div className="w-[260px] shrink-0 text-gray-500 text-sm">{example.title}</div>
            <pre className="flex-1 min-w-0 m-0 bg-gray-50 border-[1px] border-gray-200 rounded-md px-2 py-1 text-xs overflow-x-auto whitespace-pre">{curl(example.path, displayedToken)}</pre>
            <CopyToClipboardButton onClick={() => copyText(curl(example.path, token ?? '<token>'))} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default OrganizationApiToken
